/**
 * CPU fallback for browsers without WebGL2: the same per-pixel pipeline as the shader, in
 * JavaScript, at reduced resolution, drawn progressively in row bands so the page stays
 * responsive.
 */
import type { Complex } from '../geometry/complex';
import { type ModelId, toDisk } from '../geometry/models';
import { pairPermutations, invertPerm } from '../geometry/colouring';
import { type FrameParams, type SceneSettings, averageColour, triangleSize } from './scene';
import type { RGB } from './palette';

export interface MotifPixels {
  width: number;
  height: number;
  /** Premultiplied RGBA in [0, 1]. */
  data: Float32Array;
}

/** Converts canvas ImageData (straight alpha, bytes) to premultiplied floats. */
export function premultiply(img: {
  width: number;
  height: number;
  data: Uint8ClampedArray;
}): MotifPixels {
  const n = img.width * img.height;
  const data = new Float32Array(n * 4);
  for (let i = 0; i < n; i++) {
    const a = img.data[i * 4 + 3] / 255;
    data[i * 4] = (img.data[i * 4] / 255) * a;
    data[i * 4 + 1] = (img.data[i * 4 + 1] / 255) * a;
    data[i * 4 + 2] = (img.data[i * 4 + 2] / 255) * a;
    data[i * 4 + 3] = a;
  }
  return { width: img.width, height: img.height, data };
}

function sampleBilinear(m: MotifPixels, u: number, v: number, out: Float32Array): void {
  const x = Math.max(0, Math.min(m.width - 1.001, u * m.width - 0.5));
  const y = Math.max(0, Math.min(m.height - 1.001, v * m.height - 0.5));
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const fx = x - x0;
  const fy = y - y0;
  const i00 = (y0 * m.width + x0) * 4;
  const i10 = i00 + 4;
  const i01 = i00 + m.width * 4;
  const i11 = i01 + 4;
  const d = m.data;
  for (let c = 0; c < 4; c++) {
    out[c] =
      (d[i00 + c] * (1 - fx) + d[i10 + c] * fx) * (1 - fy) +
      (d[i01 + c] * (1 - fx) + d[i11 + c] * fx) * fy;
  }
}

interface Job {
  params: FrameParams;
  width: number;
  height: number;
  row: number;
  image: ImageData;
  prevRow: (Complex | null)[];
}

export class CPURenderer {
  readonly canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private settings: SceneSettings | null = null;
  private motif: MotifPixels | null = null;
  private job: Job | null = null;
  private pairs: number[][] | null = null;
  /** Maximum rendered pixels per frame (resolution is reduced to fit). */
  maxPixels = 150_000;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D is not available');
    this.ctx = ctx;
  }

  setScene(s: SceneSettings): void {
    this.settings = s;
    this.pairs = s.colouring ? pairPermutations(s.colouring) : null;
    this.job = null;
  }

  setMotif(m: MotifPixels): void {
    this.motif = m;
    this.job = null;
  }

  /** Starts (or restarts) a progressive render. */
  start(f: FrameParams): void {
    const ratio = Math.min(
      f.pixelRatio,
      Math.sqrt(this.maxPixels / Math.max(1, f.camera.width * f.camera.height)),
    );
    const width = Math.max(1, Math.round(f.camera.width * ratio));
    const height = Math.max(1, Math.round(f.camera.height * ratio));
    if (this.canvas.width !== width || this.canvas.height !== height) {
      this.canvas.width = width;
      this.canvas.height = height;
    }
    this.job = {
      params: { ...f, pixelRatio: ratio },
      width,
      height,
      row: 0,
      image: this.ctx.createImageData(width, height),
      prevRow: [],
    };
  }

  get hasJob(): boolean {
    return this.job !== null;
  }

  get busy(): boolean {
    return this.job !== null && this.job.row < this.job.height;
  }

  /** Renders rows until the time budget is spent. Returns true when the picture is complete. */
  step(budgetMs = 10): boolean {
    const job = this.job;
    const s = this.settings;
    if (!job || !s || !this.motif) return true;
    const t0 = performance.now();
    const startRow = job.row;
    while (job.row < job.height && performance.now() - t0 < budgetMs) {
      this.renderRow(job, s, job.row);
      job.row++;
    }
    this.ctx.putImageData(job.image, 0, 0, 0, startRow, job.width, job.row - startRow);
    return job.row >= job.height;
  }

  private diskAt(f: FrameParams, w: number, h: number, x: number, y: number): Complex | null {
    const cam = f.camera;
    const scale = cam.scale / f.pixelRatio;
    const m = { re: cam.cx + (x + 0.5 - w / 2) * scale, im: cam.cy + (h / 2 - y - 0.5) * scale };
    return toDisk(f.model as ModelId, m, f.morph);
  }

  private renderRow(job: Job, s: SceneSettings, y: number): void {
    const { params: f, width: w, height: h } = job;
    const data = job.image.data;
    const row: (Complex | null)[] = [];
    for (let x = 0; x <= w; x++) row.push(this.diskAt(f, w, h, x, y));
    const below: (Complex | null)[] = [];
    for (let x = 0; x <= w; x++) below.push(this.diskAt(f, w, h, x, y + 1));
    const tg = s.tg;
    const pal = s.palette;
    const avg = averageColour(s);
    const triSize = triangleSize(tg);
    const nx = tg.n1.re;
    const ny = tg.n1.im;
    const ccx = tg.center.re;
    const ccy = tg.center.im;
    const rr = tg.radius * tg.radius;
    const va = f.viewInverse.a;
    const vb = f.viewInverse.b;
    const box = s.box;
    const motif = this.motif!;
    const sample = new Float32Array(4);
    const bg = f.background;
    const scheme = s.scheme === 'parity' ? 1 : s.scheme === 'classes' && s.colouring ? 2 : 0;
    const baseInv = f.baseInverse;
    const lw = f.pixelRatio; // one CSS pixel in render pixels
    for (let x = 0; x < w; x++) {
      const di = (y * w + x) * 4;
      const z = row[x];
      let col: RGB;
      if (!z) {
        data[di] = bg[0] * 255;
        data[di + 1] = bg[1] * 255;
        data[di + 2] = bg[2] * 255;
        data[di + 3] = bg[3] * 255;
        continue;
      }
      const zx = row[x + 1];
      const zy = below[x];
      let pix = 1e-3;
      if (zx) pix = Math.hypot(zx.re - z.re, zx.im - z.im);
      if (zy) pix = Math.max(pix, Math.hypot(zy.re - z.re, zy.im - z.im));
      const r2 = z.re * z.re + z.im * z.im;
      const footH = (pix * 2) / (1 - r2);
      const fade = smoothstep(0.55, 1.9, footH / triSize);
      if (fade >= 1) {
        col = avg;
      } else {
        // w = (a z + b) / (b̄ z + ā)
        const nr = va.re * z.re - va.im * z.im + vb.re;
        const ni = va.re * z.im + va.im * z.re + vb.im;
        const dr = vb.re * z.re + vb.im * z.im + va.re;
        const dim = vb.re * z.im - vb.im * z.re - va.im;
        const dd = dr * dr + dim * dim;
        let wx = (nr * dr + ni * dim) / dd;
        let wy = (ni * dr - nr * dim) / dd;
        let count = 0;
        let m2c = 0;
        let first = -1;
        let edgeH = 0;
        const word: number[] = [];
        for (let i = 0; i < 160; i++) {
          let mir = -1;
          if (wy < 0) {
            wy = -wy;
            mir = 0;
          } else {
            const d = wx * nx + wy * ny;
            if (d > 0) {
              wx -= 2 * d * nx;
              wy -= 2 * d * ny;
              mir = 1;
            } else {
              const ex = wx - ccx;
              const ey = wy - ccy;
              const q = ex * ex + ey * ey;
              if (q < rr) {
                if (m2c === 0)
                  edgeH = Math.asinh((rr - q) / (tg.radius * (1 - (wx * wx + wy * wy))));
                const k = rr / q;
                wx = ccx + ex * k;
                wy = ccy + ey * k;
                mir = 2;
                m2c++;
              }
            }
          }
          if (mir < 0) break;
          if (count === 0) first = mir;
          count++;
          if (scheme === 2) word.push(mir);
        }
        const parity = count & 1;
        const fx = wx;
        const fy = wy; // folded into the triangle F
        if (parity === 1 && s.rotation) wy = -wy;
        let ground = pal.ground;
        let ink = pal.paint[0];
        if (scheme === 1 && !s.rotation && parity === 1) {
          ground = pal.paint[0];
          ink = pal.ground;
        } else if (scheme === 2 && this.pairs) {
          if (word.length % 2 === 1) word.push(0);
          let P = Array.from({ length: s.colouring!.k }, (_, i) => i);
          for (let i = 0; i < word.length; i += 2) {
            const pr = this.pairs[3 * word[i + 1] + word[i]];
            P = P.map((v) => pr[v]);
          }
          let cls = invertPerm(P)[0];
          if (baseInv.length > cls) cls = baseInv[cls];
          ground = pal.classGround[cls];
          ink = pal.classInk[cls];
        }
        sampleBilinear(
          motif,
          (wx - box.x0) / (box.x1 - box.x0),
          (wy - box.y0) / (box.y1 - box.y0),
          sample,
        );
        const ivory = Math.max(sample[3] - sample[0] - sample[1] - sample[2], 0);
        const base = count === 0 || (s.rotation && count === 1 && first === 0);
        // distances (in render pixels) from the folded point to the mirrors
        const om = 1 - (fx * fx + fy * fy);
        const d0 = Math.asinh((2 * Math.abs(fy)) / om) / footH;
        const d1 = Math.asinh((2 * Math.abs(fx * nx + fy * ny)) / om) / footH;
        const ex2 = fx - ccx;
        const ey2 = fy - ccy;
        const d2 = Math.asinh(Math.abs(ex2 * ex2 + ey2 * ey2 - rr) / (tg.radius * om)) / footH;
        let gridA = 0;
        if (s.grid === 'tiles') gridA = lineAlpha(d2, 0.5 * lw);
        if (s.grid === 'mirrors')
          gridA = Math.max(lineAlpha(d2, 0.55 * lw), 0.75 * lineAlpha(Math.min(d0, d1), 0.4 * lw));
        gridA *= 1 - fade;
        let tileA = 0;
        if (m2c === 0) tileA = lineAlpha(d2, 1.1 * lw) * f.tileOutline;
        else if (m2c === 1) tileA = lineAlpha(edgeH / footH, 1.1 * lw) * f.tileOutline;
        tileA *= 1 - smoothstep(0.6, 1.4, footH / triSize);
        let domA = 0;
        if (base) {
          const dd = s.rotation ? Math.min(d1, d2) : Math.min(d0, d1, d2);
          domA =
            lineAlpha(dd, 0.6 * lw * Math.max(1, f.domainOutline)) * Math.min(f.domainOutline, 1);
        }
        col = [0, 0, 0];
        for (let c = 0; c < 3; c++) {
          let v =
            ground[c] * (1 - sample[3]) +
            ink[c] * sample[0] +
            pal.paint[1][c] * sample[1] +
            pal.paint[2][c] * sample[2] +
            pal.paint[3][c] * ivory;
          if (f.focus > 0 && !base) v += (pal.ground[c] - v) * 0.7 * f.focus;
          v = v * (1 - fade) + avg[c] * fade;
          v += (pal.gold[c] * 0.92 - v) * gridA * 0.85;
          v += (pal.line[c] - v) * domA * 0.8;
          v += (pal.line[c] - v) * tileA * 0.9;
          col[c] = v;
        }
      }
      const vignette = 1 - 0.1 * smoothstep(0.82, 1, Math.sqrt(r2));
      col = [col[0] * vignette, col[1] * vignette, col[2] * vignette];
      const edge = (1 - Math.sqrt(r2)) / Math.max(pix, 1e-9);
      const rim = Math.max(0, Math.min(1, 1.1 - edge));
      data[di] = (col[0] * (1 - rim) + pal.gold[0] * rim) * 255;
      data[di + 1] = (col[1] * (1 - rim) + pal.gold[1] * rim) * 255;
      data[di + 2] = (col[2] * (1 - rim) + pal.gold[2] * rim) * 255;
      data[di + 3] = 255;
    }
    job.prevRow = row;
  }
}

function lineAlpha(distPx: number, halfWidthPx: number): number {
  return Math.max(0, Math.min(1, halfWidthPx + 0.5 - distPx));
}

function smoothstep(a: number, b: number, x: number): number {
  const t = Math.max(0, Math.min(1, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
}
